export const INTERNATIONAL_CHECKOUT_PREF_KEY = 'gab_prefer_international_checkout';

export function readPreferInternationalCheckout() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(INTERNATIONAL_CHECKOUT_PREF_KEY) === '1';
  } catch {
    return false;
  }
}

export function writePreferInternationalCheckout() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INTERNATIONAL_CHECKOUT_PREF_KEY, '1');
  } catch {
    // localStorage can be blocked; checkout still works for this session.
  }
}

/**
 * Decide which web checkout provider to use.
 * International users, buyers who asked for international checkout, and
 * known Whop Plus subscribers always use Whop. US users use Valmar unless
 * it is blocked.
 */
export function resolveWebCheckoutProvider({
  isInternational,
  valmarAvailable,
  isWhopSubscriber = false,
  preferInternational = false,
}) {
  if (isWhopSubscriber) return 'whop';
  if (preferInternational) return 'whop';
  if (isInternational) return 'whop';
  if (!valmarAvailable) return 'whop';
  return 'valmar';
}

/**
 * Whether to speculatively warm the Valmar availability probe.
 *
 * This is deliberately a named, tested decision rather than a condition inside the
 * effect, because the cost of getting it wrong is invisible in review. The probe is not
 * a ping: it injects the tokenizer script, opens two cross-origin iframes into the
 * payment processor and instantiates a live tokenizer, which pulls a 3-D Secure SDK and
 * a Cloudflare challenge script with it.
 *
 * `GabPurchaseProvider` is mounted at the app root, so without the `isAuthenticated`
 * gate every anonymous visitor paid all of that on whatever marketing page they landed
 * on — during the window that decides LCP, and on the pages where SEO matters most.
 *
 * Skipping it is safe rather than merely cheap: no anonymous visitor can reach checkout
 * (every entry point routes them to /signup first), and `resolveCheckoutProvider()`
 * calls the same cached `checkValmarAvailable()` on demand when checkout does open. The
 * prefetch only ever buys latency.
 */
export function shouldPrefetchValmarAvailability({
  platform,
  isAuthenticated,
  ipCheckDone,
  isInternational,
  isWhopSubscriber,
} = {}) {
  if (platform !== 'web') return false;
  if (!isAuthenticated) return false;
  // Geo decides whether Valmar is even a candidate; probing before it resolves would
  // hit the processor for buyers who are going to Whop regardless.
  if (!ipCheckDone) return false;
  if (isInternational) return false;
  if (isWhopSubscriber) return false;
  return true;
}

export function isValmarBlockedError(payload = {}) {
  if (payload.valmarBlocked) return true;

  const message = String(payload.errorMessage || '').toLowerCase();
  return (
    message.includes('valmar')
    || message.includes('tokenizer')
    || message.includes('card form could not load')
    || message.includes('script blockers')
  );
}

/**
 * Whether the buyer should be treated as international, using the country our
 * own server resolved from the request (Cloudflare header, then IP lookup).
 *
 * Returns null when the server told us nothing, leaving the caller to fall back
 * to the third-party IP lookup. `countryCode` is preferred over the boolean
 * because the server reports `isInternational: false` for an unresolvable
 * country, which must not be read as a confirmed US buyer.
 */
export function resolveIsInternationalFromServer({ countryCode, isInternational } = {}) {
  const code = typeof countryCode === 'string' ? countryCode.trim().toUpperCase() : '';
  if (code) return code !== 'US';

  // An explicit null means the server tried and could not resolve the country.
  // Report unknown rather than reading the companion boolean, which is false in
  // exactly this case and would look like a confirmed US buyer.
  if (countryCode === null) return null;

  // No country field at all — an older payload that only carries the boolean.
  if (typeof isInternational === 'boolean') return isInternational;
  return null;
}

export function resolvePaymentRetrySettingsPage(checkoutType) {
  return checkoutType === 'credits' ? 'credits' : 'subscription';
}

// Deliberately does not promise "no charge was made": a 3D Secure attempt that
// never finishes often leaves an authorization hold on the buyer's card.
export const WHOP_PAYMENT_ERROR_FALLBACK = 'Your payment could not be completed. Please try again or use a different card.';

/**
 * Whop's embed reports declines, 3D Secure failures and failed returns from
 * external payment providers through its onPaymentError callback. Its `message`
 * is written for buyers, so surface that verbatim and only fall back when the
 * embed hands us nothing readable.
 */
export function describeWhopPaymentError(error) {
  if (typeof error === 'string') {
    return error.trim() || WHOP_PAYMENT_ERROR_FALLBACK;
  }

  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (message) return message;

  const code = typeof error?.code === 'string' ? error.code.trim() : '';
  if (code) return `${WHOP_PAYMENT_ERROR_FALLBACK} (${code})`;

  return WHOP_PAYMENT_ERROR_FALLBACK;
}
